import React, { lazy, Suspense, useRef, useState, useEffect, useMemo } from "react";
import Helmet from "react-helmet";
import CacheBuster from "react-cache-buster";
import { Spinner } from "react-bootstrap";
import appversion from "../package.json";
import { useLocation } from "react-router-dom";
import FooterDisclaimer from "./components/FooterDisclaimer/FooterDisclaimer";
import "./App.css";

// Lazy loaded components
const BrokerBanner = lazy(() => import("./components/Banner/BrokerBanner"));
const TruApproval = lazy(() => import("./components/TruApproval/TruApproval"));
const LoanPrograms = lazy(() => import("./components/LoanPrograms/index"));
const AboutSection = lazy(() => import("./components/AboutSection/index"));

const BrokerpagesContent = ({ brokerData, loading, error, brokerUrl }) => {
  const scrollRef = useRef(null);
  const appVersion = appversion.version;
  const [showFooter, setShowFooter] = useState(false);

  const productSectionRef = useRef(null);
  // Check if the location state has scrollToProductSection
  const location = useLocation();

  // Function to extract username from URL (similar to your original getUserNameFromURL)
  const getUserNameFromURL = () => {
    try {
      const pathSegments = window.location.pathname.split('/').filter(segment => segment);
      // Assuming URL structure is /partner/{username}/
      const partnerIndex = pathSegments.indexOf('partner');
      if (partnerIndex !== -1 && pathSegments[partnerIndex + 1]) {
        return pathSegments[partnerIndex + 1];
      }
      return null;
    } catch (error) {
      console.error('Error extracting username from URL:', error);
      return null;
    }
  };

  // Create canonical URL using useMemo for performance
  const canonicalUrl = useMemo(() => {
    try {
      const userName = getUserNameFromURL();
      
      if (!userName) {
        console.error('Invalid username in URL');
        return null;
      }

      // Clean and validate the username
      const cleanUserName = encodeURIComponent(userName.trim());
      
      // Construct the canonical URL
      const protocol = 'https://';
      const domain = 'www.angelai.com';
      const basePath = '/partner/';
      const canonicalUrl = `${protocol}${domain}${basePath}${cleanUserName}/`;

      // Validate the constructed URL
      try {
        new URL(canonicalUrl);
        return canonicalUrl;
      } catch (e) {
        console.error('Invalid URL constructed:', canonicalUrl);
        return null;
      }
    } catch (error) {
      console.error('Error creating canonical URL:', error);
      return null;
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (location.state?.scrollToProductSection) {
      productSectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  React.useEffect(() => {
    // Handles scrolling to loan product section
    const whereTo = scrollRef?.current ? true : false;
    if (whereTo) {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      window.scrollTo({
        top: scrollRef.current.top + scrollTop,
      });
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [scrollRef]);

  // Add scroll listener to detect when to show footer
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.body.scrollHeight;
      const scrollPosition = window.scrollY;

      // Show footer when user is within 300px of the bottom
      // or has scrolled past 80% of the page
      const isBottom = scrollPosition + windowHeight >= documentHeight - 300;
      const scrollPercentage = (scrollPosition / (documentHeight - windowHeight)) * 100;

      if (isBottom || scrollPercentage > 80) {
        setShowFooter(true);
      }
    };

    window.addEventListener("scroll", handleScroll);

    // Check initial scroll position
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleScrollRef = (value) => (scrollRef.current = value);

  if (loading) {
    return <div><Spinner /></div>;
  }

  if (error) {
    return <div>Error loading broker data: {error}</div>;
  }

  if (!brokerData) {
    return <div>No broker data available</div>;
  }

  return (
    <CacheBuster
      currentVersion={appVersion}
      isEnabled={true}
      isVerboseMode={false}
      metaFileDirectory={process.env.PUBLIC_URL}
    >
      <Helmet>
        <title>{brokerData?.name ? `${brokerData.name} | ${brokerData?.companyName}` : "Find Your Dream Home with a Trusted Mortgage Partner"}</title>
        <meta
          name="description"
          content={`Expert mortgage solutions from ${brokerData?.name}. Explore personalized home loan options to help you secure your dream home.`}
        />
        {/* Canonical URL */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        
        <meta property="og:url" content={canonicalUrl || window.location.href} />
        <meta property="og:title" content={brokerData?.name ? `${brokerData.name} | ${brokerData?.companyName}` : "Find Your Dream Home with a Trusted Mortgage Partner"} />
        <meta property="og:description" content={`Expert mortgage solutions from ${brokerData?.name}. Explore personalized home loan options to help you secure your dream home.`} />
        <meta property="og:keywords" content="Mortgage loan officer, Broker, Home loans, Mortgage expert, Buy a home, Mortgage pre-approval, Home financing, Mortgage application, Mortgage help, Home loan guide, Personalized mortgage support, FHA loans, VA loans, Conventional loans, USDA loans, Jumbo loans, Non-QM loans, Down payment assistance, Renovation loans, HELOC, Reverse mortgage, Refinancing, Licensed loan officer, Mortgage lender, Home loan specialist, AngelAi mortgage, AngelAi loan officer, Powered by AngelAi, TRU ApprovalⓇ, Fast mortgage closings, Digital mortgage assistant, AI mortgage support, Mortgage tools online, AI home loan solutions, Trusted mortgage partner, Expert loan advice, Licensed mortgage professional, NMLS certified, Real estate financing, Chat, Bot, AngelAi, Assistant, Empathetic Technology, 24/7, Mortgage, Loan, Underwriter, Loan Assistant, Homebuying, Homebuyer, Home Loan, Real Estate, Real Estate Deals, Loan Originator, Lender, Loan Keeping, Instant Underwriting, Realtor, Mortgage Technology, Blockchain Technology, The Processor, Low Rates, Homebuyer Connect, Wholesale, Correspondent, Faster Conditions, Quick Turn Times, Break Free, Underwriter in Your Pocket, Pocket Underwriter, Underwriter at Your Fingertips, useAngelAi.com, Quick Underwriting, Ask AngelAi" />
        <meta name="twitter:url" content={canonicalUrl || window.location.href} />
        <meta name="twitter:title" content={brokerData?.name ? `${brokerData.name} | ${brokerData?.companyName}` : "Find Your Dream Home with a Trusted Mortgage Partner"} />
        <meta name="twitter:description" content={`Expert mortgage solutions from ${brokerData?.name}. Explore personalized home loan options to help you secure your dream home.`}  />
      </Helmet>
      <>
        <Suspense fallback={<div><Spinner /></div>}>
          <BrokerBanner brokerData={brokerData} />
          <Suspense fallback={<div><Spinner /></div>}>
            <TruApproval brokerData={brokerData} />
            <Suspense fallback={<div><Spinner /></div>}>
              <div ref={productSectionRef}>
                <LoanPrograms
                  fetchpriority="low"
                  brokerData={brokerData}
                  handleScrollRef={handleScrollRef}
                />
              </div>
              <Suspense fallback={<div><Spinner /></div>}>
                <AboutSection brokerData={brokerData} />
                {/* Only render footer when user has scrolled to the bottom */}
                {showFooter && (
                  <Suspense fallback={<div><Spinner /></div>}>
                    <FooterDisclaimer brokerData={brokerData} brokerUrl={brokerUrl} />
                  </Suspense>
                )}
              </Suspense>
            </Suspense>
          </Suspense>
        </Suspense>
      </>
    </CacheBuster>
  );
};

export default BrokerpagesContent;